import React, { useState, useEffect } from 'react';
import { Icon, Button, Badge } from '@/components/atoms';
import { Modal } from '@/components/molecules';
import { useUpdateSaleStatusMutation, useInvoiceRequirementsQuery } from '@/hooks/useErpApi';
import { erpClient } from '@/services/clients';
import { useAppDispatch } from '@/hooks';
import { useAppSelector } from '@/hooks';
import { updateSale } from '@/features/sales';
import { CancelOrderModal } from './CancelOrderModal';
import { PartialCancelModal } from './PartialCancelModal';
import { config } from '@/config/env';
import type { SaleResponse } from '@kisanlink/erp-service';

interface SalesOrderDetailModalProps {
  sale: SaleResponse | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const SalesOrderDetailModal: React.FC<SalesOrderDetailModalProps> = ({
  sale,
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const { mutateAsync: updateSaleStatusMutation, isLoading: isUpdatingStatus } = useUpdateSaleStatusMutation();
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>(sale?.status || 'pending');
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<string>(sale?.payment_mode || 'cash');
  const [isUpdatingPaymentMode, setIsUpdatingPaymentMode] = useState(false);
  const [cancelOrderOpen, setCancelOrderOpen] = useState(false);
  const [partialCancelOpen, setPartialCancelOpen] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  
  const isLoading = isUpdatingStatus || isUpdatingPaymentMode;

  // Check invoice requirements
  const { data: invoiceRequirements, isLoading: isLoadingRequirements } = useInvoiceRequirementsQuery({
    enabled: open && !!sale, // Only check when modal is open and sale exists
  });

  const invoiceReady = (invoiceRequirements as any)?.data?.ready ?? false;
  const missingSettings = (invoiceRequirements as any)?.data?.missing_settings ?? [];

  // Check if sale can be cancelled (only pending or processing)
  const canCancel = sale && ['pending', 'processing'].includes(sale.status);

  // Sync selected status and payment mode with sale prop
  useEffect(() => {
    if (sale) {
      setSelectedStatus(sale.status);
      setSelectedPaymentMode(sale.payment_mode);
    }
  }, [sale]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCancelOrderOpen(false);
      setPartialCancelOpen(false);
    }
  }, [open]);

  if (!sale) return null;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'cancelled':
        return <Badge variant="error">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setError(null);
      const result = await updateSaleStatusMutation({
        id: sale.id,
        status: selectedStatus,
      });
      dispatch(updateSale(result as never));
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sale status');
    }
  };

  const handleUpdatePaymentMode = async () => {
    if (!sale || selectedPaymentMode === sale.payment_mode) return;

    try {
      setError(null);
      setIsUpdatingPaymentMode(true);
      // Use PATCH API to update payment_mode (frontend-only endpoint)
      const baseURL = config.erpServiceUrl.replace(/\/$/, '');
      const response = await fetch(`${baseURL}/api/v1/sales/${sale.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify({ payment_mode: selectedPaymentMode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update payment mode');
      }

      const result = await response.json();
      if (result?.data) {
        dispatch(updateSale(result.data as never));
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update payment mode');
    } finally {
      setIsUpdatingPaymentMode(false);
    }
  };

  const handleCancelSuccess = () => {
    setCancelOrderOpen(false);
    setPartialCancelOpen(false);
    onSuccess();
  };

  const handleDownloadInvoice = async () => {
    if (!sale) return;

    setIsDownloadingInvoice(true);
    setError(null);

    try {
      // Generate invoice PDF
      const blob = await erpClient.sales.generateInvoice(sale.id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Try to extract filename from blob or use default
      // The backend should set Content-Disposition header, but we can't access it from blob
      // So we'll use a default filename format
      // Invoice numbers are now 12 characters (MMYYXXXXXXXX), but fallback handles both old (8) and new (12) formats
      const saleNumber = (sale as any).invoice_number || sale.id.substring(0, 12);
      a.download = `Invoice_${saleNumber}.pdf`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download invoice';
      setError(errorMessage);
      console.error('Invoice download error:', err);
    } finally {
      setIsDownloadingInvoice(false);
    }
  };

  return (
    <>
      <Modal isOpen={open} onClose={onClose} title={`Sale Order Details`} size="xl">
        <div className="space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {error && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Icon name="CircleAlert" size={20} className="text-error-600 mt-0.5" />
                <p className="text-error-700">{error}</p>
              </div>
            </div>
          )}

          {/* Cancelled Banner */}
          {sale.status === 'cancelled' && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="CircleX" size={20} className="text-error-600" />
                <p className="text-sm font-medium text-error-700">This order has been cancelled</p>
              </div>
            </div>
          )}

          {/* Sale Header with Cancel Buttons */}
          <div className="bg-neutral-50 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {(sale as any).invoice_number || `SO-${sale.id.substring(0, 12)}`}
                </h3>
                <p className="text-neutral-600 text-sm mt-1">
                  {sale.sale_date
                    ? `Sale Date: ${new Date(sale.sale_date).toLocaleDateString()} at ${new Date(sale.sale_date).toLocaleTimeString()}`
                    : `Created: ${new Date(sale.created_at).toLocaleDateString()} at ${new Date(sale.created_at).toLocaleTimeString()}`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(sale.status)}
                {/* Cancel Buttons */}
                {canCancel && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPartialCancelOpen(true)}
                      className="text-warning-600 hover:text-warning-700 hover:bg-warning-50"
                    >
                      <Icon name="Minus" size={14} className="mr-1" />
                      Cancel Items
                    </Button>
                    <Button variant="error" size="sm" onClick={() => setCancelOrderOpen(true)}>
                      <Icon name="X" size={14} className="mr-1" />
                      Cancel Order
                    </Button>
                  </div>
                )}
                {!canCancel && sale.status !== 'cancelled' && (
                  <span
                    className="text-xs text-neutral-500 ml-4"
                    title="Shipped or delivered orders cannot be cancelled"
                  >
                    Cancellation not available
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-neutral-600 mb-1">Warehouse ID</p>
                <p className="text-sm font-medium text-neutral-900">{sale.warehouse_id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-600 mb-1">Customer</p>
                <p className="text-sm font-medium text-neutral-900">
                  {sale.customer_name || sale.customer_phone
                    ? `${sale.customer_name || 'Customer'} ${sale.customer_phone ? `(${sale.customer_phone})` : ''}`
                    : 'Walk-in'}
                </p>
                {sale.is_org_member && (
                  <p className="text-xs text-success-600 mt-1">FPO Member</p>
                )}
              </div>
              <div>
                <p className="text-xs text-neutral-600 mb-1">Sale Type</p>
                <p className="text-sm font-medium text-neutral-900 capitalize">{sale.sale_type}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-600 mb-1">Payment Mode</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedPaymentMode}
                      onChange={(e) => setSelectedPaymentMode(e.target.value)}
                      disabled={isUpdatingPaymentMode}
                      className="text-sm font-medium text-neutral-900 uppercase border border-neutral-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="online">Online</option>
                    </select>
                    {isUpdatingPaymentMode && (
                      <Icon name="Loader" size={14} className="animate-spin text-primary-600" />
                    )}
                  </div>
                  {selectedPaymentMode !== sale?.payment_mode && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleUpdatePaymentMode}
                      disabled={isUpdatingPaymentMode}
                      className="flex items-center gap-2"
                    >
                      <Icon name="Check" size={14} />
                      Update Payment Mode
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <h4 className="text-base font-semibold text-neutral-900 mb-3">Order Items</h4>
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" style={{ minWidth: '800px' }}>
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700">
                      Batch ID
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                      Selling Price
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                      Cost Price
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                      Margin
                    </th>
                    {sale.apply_taxes && (
                      <>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                          CGST
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                          SGST
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                          IGST
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                          Tax Total
                        </th>
                      </>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-700">
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {sale.items && sale.items.length > 0 ? (
                    sale.items.map((item) => (
                      <tr key={item.id}>
                        <td className="px-4 py-3 text-sm text-neutral-900 font-mono">
                          {(item as any).sku || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-900 font-mono">
                          {item.batch_id?.substring(0, 12) || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                          ₹{item.selling_price.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                          ₹{item.cost_price.toFixed(2)}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${
                          item.margin >= 0 ? 'text-success-600' : 'text-error-600'
                        }`}>
                          ₹{item.margin.toFixed(2)}
                        </td>
                        {sale.apply_taxes && (
                          <>
                            <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                              ₹{item.cgst_amount.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                              ₹{item.sgst_amount.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                              ₹{((item as any).igst_amount || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-neutral-900 text-right">
                              ₹{item.total_tax_amount.toFixed(2)}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3 text-sm font-medium text-neutral-900 text-right">
                          ₹{item.line_total.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={sale.apply_taxes ? 10 : 6}
                        className="px-4 py-3 text-center text-sm text-neutral-500"
                      >
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-neutral-50">
                  <tr>
                    <td
                      colSpan={sale.apply_taxes ? 10 : 6}
                      className="px-4 py-3 text-right text-sm font-semibold text-neutral-900"
                    >
                      Total Amount:
                    </td>
                    <td className="px-4 py-3 text-right text-base font-bold text-neutral-900">
                      ₹{sale.total_amount.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              </div>
            </div>
          </div>

          {/* Breakdown */}
          {sale.breakdown && (
            <div className="bg-info-50 border border-info-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-info-900 mb-3">Price Breakdown</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-info-700">Base Amount:</span>
                  <span className="font-medium text-info-900">
                    ₹{sale.breakdown.base_amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-info-700">Tax Amount:</span>
                  <span className="font-medium text-info-900">
                    ₹{sale.breakdown.tax_amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-info-700">Discount:</span>
                  <span className="font-medium text-success-700">
                    -₹{sale.breakdown.discount_amount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-info-700">Final Amount:</span>
                  <span className="font-bold text-info-900">
                    ₹{sale.breakdown.final_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Status Update */}
          {sale.status !== 'cancelled' && (
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-neutral-900 mb-3">Update Status</h4>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label
                    htmlFor="status"
                    className="block text-xs font-medium text-neutral-700 mb-2"
                  >
                    New Status
                  </label>
                  <select
                    id="status"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={isLoading}
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <Button
                  variant="primary"
                  onClick={handleUpdateStatus}
                  disabled={isLoading || selectedStatus === sale.status}
                >
                  {isLoading ? (
                    <>
                      <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Icon name="Check" size={16} className="mr-2" />
                      Update Status
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Invoice Download Section */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-neutral-900 mb-3">Invoice</h4>
            {isLoadingRequirements ? (
              <div className="flex items-center gap-2 text-sm text-neutral-600">
                <Icon name="Loader" size={16} className="animate-spin" />
                Checking requirements...
              </div>
            ) : invoiceReady ? (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm text-success-700">All invoice requirements are met</p>
                </div>
                <Button
                  variant="primary"
                  onClick={handleDownloadInvoice}
                  disabled={isDownloadingInvoice || isLoading}
                >
                  {isDownloadingInvoice ? (
                    <>
                      <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Icon name="Download" size={16} className="mr-2" />
                      Download Invoice
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon name="CircleAlert" size={20} className="text-warning-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-warning-900 mb-1">
                        Invoice requirements not met
                      </p>
                      <p className="text-xs text-warning-700 mb-2">
                        Please configure the following settings to generate invoices:
                      </p>
                      <ul className="list-disc list-inside text-xs text-warning-700 space-y-1">
                        {missingSettings.map((setting: string) => (
                          <li key={setting}>
                            {setting
                              .replace('fpo_', '')
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    window.location.href = '/settings';
                  }}
                >
                  <Icon name="Settings" size={16} className="mr-2" />
                  Configure Settings
                </Button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t border-neutral-200">
            <Button variant="secondary" onClick={onClose} disabled={isLoading || isDownloadingInvoice}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Order Modal */}
      <CancelOrderModal
        sale={sale}
        isOpen={cancelOrderOpen}
        onClose={() => setCancelOrderOpen(false)}
        onSuccess={handleCancelSuccess}
      />

      {/* Partial Cancel Modal */}
      <PartialCancelModal
        sale={sale}
        isOpen={partialCancelOpen}
        onClose={() => setPartialCancelOpen(false)}
        onSuccess={handleCancelSuccess}
      />
    </>
  );
};
